<template>
    <v-container>
        <v-row>
            <v-col>
                <v-row>
                    <v-col>
                        <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
                    </v-col>
                    <v-col>
                        <v-btn class="add-new" color="primary" @click="goToCreateNew()">Ny profil</v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-card>
                            <v-card-title>
                                <v-text-field
                                    v-model="store.searchPhrase"
                                    append-inner-icon="mdi-magnify"
                                    clearable
                                    hide-details
                                    label="Søk"
                                    single-line
                                    variant="underlined"
                                ></v-text-field>
                            </v-card-title>
                            <v-data-table-server
                                :headers="headers"
                                :items="store.profiles"
                                :items-length="store.paging.totalItemCount"
                                :items-per-page="store.paging.pageSize"
                                :items-per-page-options="DefaultPerPageOptions"
                                :loading="isLoading"
                                :page="store.paging.pageNumber"
                                :search="store.searchPhrase"
                                @update:items-per-page="onItemsPerPageChange"
                                @update:page="onPageChange"
                            >
                                <template #item.phone="{ item }">
                                    {{ formatters.toFullPhone(item.phone) }}
                                </template>
                                <template #item.gdprConsentAt="{ item }">
                                    {{ formatDate(item.gdprConsentAt) }}
                                </template>
                                <template #item.controls="{ item }">
                                    <router-link :to="{ name: ProfileRels.UpdateProfile, params: { id: item.id } }">
                                        <v-icon class="edit" icon="mdi-account-edit" />
                                    </router-link>
                                </template>
                            </v-data-table-server>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts" setup>
import { useProfilesStore } from '@/features/profiles/profilesStore';
import { onMounted, ref, watch } from 'vue';
import { useFormatters } from '@/features/shared/formatters';
import { ProfileRels } from '@/features/profiles/rels';
import { useRoute, useRouter } from 'vue-router';
import { VDataTableServer } from 'vuetify/components';
import { DefaultPaging, DefaultPerPageOptions } from '@/features/shared/models';
import { storeToRefs } from 'pinia';
import _ from 'lodash';

const store = useProfilesStore();
const { searchPhrase } = storeToRefs(store);
const formatters = useFormatters();
const router = useRouter();
const route = useRoute();

const breadcrumbs = [
    {
        title: 'Profiler'
    }
];

const headers = [
    {
        title: 'Fornavn',
        key: 'firstName',
        sortable: false
    },
    {
        title: 'Etternavn',
        key: 'lastName',
        sortable: false
    },
    {
        title: 'E-post',
        key: 'email',
        sortable: false
    },
    {
        title: 'Telefon',
        key: 'phone',
        sortable: false
    },
    {
        title: 'Jobb e-post',
        key: 'workEmail',
        sortable: false
    },
    {
        title: 'GDPR-samtykke',
        key: 'gdprConsentAt',
        sortable: false
    },
    {
        title: '',
        key: 'controls',
        sortable: false
    }
];

const isLoading = ref(true);
let initialized = false;

onMounted(async () => {
    isLoading.value = true;

    if (route.query?.search) {
        store.searchPhrase = route.query.search.toString();
    } else {
        store.searchPhrase = '';
    }
    if (route.query?.page) {
        store.paging.pageNumber = Number(route.query.page);
    } else {
        store.paging.pageNumber = DefaultPaging.pageNumber;
    }
    if (route.query?.pageSize) {
        store.paging.pageSize = Number(route.query.pageSize);
    } else {
        store.paging.pageSize = DefaultPaging.pageSize;
    }

    await store.loadProfiles();

    isLoading.value = false;
    initialized = true;
});

async function onPageChange(pageNumber: number) {
    if (!initialized) return;

    isLoading.value = true;

    store.paging.pageNumber = pageNumber;

    await updateRouteQuery();
    await store.loadProfiles();

    isLoading.value = false;
}

async function onItemsPerPageChange(pageSize: number) {
    if (!initialized) return;

    isLoading.value = true;

    store.paging.pageSize = pageSize;

    await updateRouteQuery();
    await store.loadProfiles();

    isLoading.value = false;
}

watch(
    searchPhrase,
    _.debounce(async () => {
        if (!initialized) return;

        isLoading.value = true;

        await updateRouteQuery();
        await store.loadProfiles();

        isLoading.value = false;
    }, 500)
);

async function updateRouteQuery() {
    const query = { page: store.paging.pageNumber, pageSize: store.paging.pageSize } as any;
    if (store.searchPhrase) query.search = store.searchPhrase;
    await router.push({ ...route, query: query });
}

function goToCreateNew() {
    router.push({ name: ProfileRels.CreateProfile });
}

function formatDate(date?: Date) {
    if (!date) return '';
    return formatters.toLocaleDateTime(date);
}
</script>

<style scoped>
.edit {
    color: #000000;
}

.add-new {
    float: right;
    margin-top: 10px;
}
</style>
